import React, { useEffect, useState } from "react";
import { Modal, Select, Button, Tag, Space } from "antd";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { FaSpinner } from "react-icons/fa";
import rbacService from "../../services/rbacService";

const UserRolesModal = ({ visible, user, onClose, onSuccess }) => {
  const [userRoles, setUserRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible && user) {
      fetchData();
    }
  }, [visible, user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [roles, assignedRoles] = await Promise.all([
        rbacService.getAllRoles(),
        rbacService.getUserRoles(user.userID),
      ]);

      setAllRoles(roles);
      setUserRoles(assignedRoles);
    } catch (error) {
      toast.error(
        <p dir="rtl" className="font-custom">
          خطا در بارگذاری اطلاعات
        </p>
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedRole) {
      toast.error(
        <p dir="rtl" className="font-custom">
          لطفا یک نقش انتخاب کنید
        </p>
      );
      return;
    }

    try {
      setSubmitting(true);
      await rbacService.assignRoleToUser(user.userID, selectedRole);

      toast.success(
        <p dir="rtl" className="font-custom">
          نقش با موفقیت تخصیص داده شد
        </p>
      );

      setSelectedRole(null);
      await fetchData();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error(
        <p dir="rtl" className="font-custom">
          خطا در تخصیص نقش
        </p>
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveRole = async (roleId) => {
    try {
      setSubmitting(true);
      await rbacService.removeRoleFromUser(user.userID, roleId);

      toast.success(
        <p dir="rtl" className="font-custom">
          نقش با موفقیت حذف شد
        </p>
      );

      await fetchData();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error(
        <p dir="rtl" className="font-custom">
          خطا در حذف نقش
        </p>
      );
    } finally {
      setSubmitting(false);
    }
  };

  const availableRoles = allRoles.filter(
    (role) => !userRoles.find((ur) => ur.id === role.id)
  );

  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        centered
        width={600}
        title={
          <div className="font-custom text-right">
            مدیریت نقش‌های کاربر: {user?.firstname} {user?.lastname}
          </div>
        }
      >
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600 font-custom">در حال بارگذاری...</p>
            </div>
          </div>
        ) : (
          <div className="font-custom space-y-4">
            {/* Current Roles */}
            <div className="text-right">
              <label className="text-sm text-gray-600 mb-2 block">
                نقش‌های فعلی:
              </label>
              {userRoles.length === 0 ? (
                <div className="text-center text-gray-500 p-4 border border-dashed rounded">
                  هیچ نقشی تخصیص داده نشده است
                </div>
              ) : (
                <div className="space-y-2">
                  {userRoles.map((role) => (
                    <div
                      key={role.id}
                      className="flex justify-between items-center p-2 bg-gray-50 rounded border"
                    >
                      <div className="text-right">
                        <div className="font-semibold">{role.name}</div>
                        {role.description && (
                          <div className="text-xs text-gray-500">
                            {role.description}
                          </div>
                        )}
                      </div>
                      <Button
                        danger
                        size="small"
                        onClick={() => handleRemoveRole(role.id)}
                        disabled={submitting || role.name === "SuperAdmin"}
                      >
                        حذف
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Assign New Role */}
            <div className="text-right">
              <label className="text-sm text-gray-600 mb-2 block">
                تخصیص نقش جدید:
              </label>
              <Space.Compact style={{ width: "100%" }}>
                <Select
                  style={{ width: "100%" }}
                  placeholder="انتخاب نقش"
                  value={selectedRole}
                  onChange={setSelectedRole}
                  className="font-custom"
                  disabled={submitting || availableRoles.length === 0}
                  options={availableRoles.map((role) => ({
                    value: role.id,
                    label: role.name,
                    className: "font-custom text-right",
                  }))}
                />
                <Button
                  type="primary"
                  onClick={handleAssignRole}
                  disabled={submitting || !selectedRole}
                  className="font-custom"
                >
                  {submitting ? <FaSpinner className="animate-spin" /> : "افزودن"}
                </Button>
              </Space.Compact>
              {availableRoles.length === 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  تمام نقش‌ها تخصیص داده شده‌اند
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        rtl
        theme="dark"
        transition={Bounce}
      />
    </>
  );
};

export default UserRolesModal;
